import log from 'loglevel';

export const mutationTypes = {
  SET_CONTENT_AND_CONTEXT: 'SET_CONTENT_AND_CONTEXT',
  SET_INITIALIZING: 'SET_INITIALIZING',
  SET_FACET_GROUPS: 'SET_FACET_GROUPS',
  SET_SEARCH_TERM: 'SET_SEARCH_TERM',
  SET_ORDER_BY: 'SET_ORDER_BY',
  SET_PAGE_NUMBER: 'SET_PAGE_NUMBER',
  SET_SELECTED_FILTERS: 'SET_SELECTED_FILTERS',
  TOGGLE_SELECTED_FILTER: 'TOGGLE_SELECTED_FILTER',
  SET_RESPONSE: 'SET_RESPONSE',
  RESET_ABORT_CONTROLLER: 'RESET_ABORT_CONTROLLER',
  SET_IS_LOADING: 'SET_IS_LOADING',
};

/**
 * @param {keyof mutationTypes} mutationType
 */
export function mutation(mutationType) {
  return `search/${mutationType}`;
}

/**
 * @type {import('vuex').MutationTree<SearchState>}
 */
export const mutations = {
  /**
   *
   * @param {SearchState} state
   * @param {{ context: import('./state').SearchContext, content: import('./state').SearchContent }} param1
   */
  [mutationTypes.SET_CONTENT_AND_CONTEXT](state, { context, content }) {
    state.culture = context.culture;
    state.content = content;
    state.cdnDomain = context.cdnDomain;
    state.request.taxonomyRootNodeGUID = context.taxonomyRootNodeGUID;
    state.request.pageSize = context.pageSize;
    state.orderByOptions.alphabetical.name = content.alphabetically;
    state.orderByOptions.relevance.name = content.relevance;
    state.queryKeyTerm = context.queryKeyTerm;
    state.queryKeyIdentifiers = context.queryKeyIdentifiers;
  },

  /**
   *
   * @param {SearchState} state
   * @param {boolean} isInitializing
   */
  [mutationTypes.SET_INITIALIZING](state, isInitializing) {
    state.actionState = isInitializing ? 'INITIALIZING' : 'LOADED';
  },

  /**
   *
   * @param {SearchState} state
   * @param {import('./state').FacetGroup[]} filters
   */
  [mutationTypes.SET_FACET_GROUPS](state, groups) {
    state.response.groups = groups;
  },

  /**
   *
   * @param {SearchState} state
   * @param {string} term
   */
  [mutationTypes.SET_SEARCH_TERM](state, term) {
    state.request.term = term;
    state.request.pageNumber = 1;
  },

  /**
   *
   * @param {SearchState} state
   * @param {string} orderBy
   */
  [mutationTypes.SET_ORDER_BY](state, orderBy) {
    state.request.orderBy = orderBy;
    state.request.pageNumber = 1;
  },

  /**
   *
   * @param {SearchState} state
   * @param {number} pageNumber
   */
  [mutationTypes.SET_PAGE_NUMBER](state, pageNumber) {
    state.request.pageNumber = pageNumber;
  },

  /**
   *
   * @param {SearchState} state
   * @param {string[]} filterIdentifiers
   */
  [mutationTypes.SET_SELECTED_FILTERS](state, filterIdentifiers) {
    state.request.filters = {};

    if (!filterIdentifiers.length) {
      return;
    }

    for (const id of filterIdentifiers) {
      for (const group of state.response.groups) {
        for (const facet of group.facets) {
          if (facet.identifier !== id) {
            continue;
          }

          if (!state.request.filters[group.value]) {
            state.request.filters[group.value] = [];
          }

          state.request.filters[group.value].push(facet.value);
        }
      }
    }
  },

  /**
   *
   * @param {SearchState} state
   * @param {{ filterId: number, groupId: number }} selection
   */
  [mutationTypes.TOGGLE_SELECTED_FILTER](state, { filterId, groupId }) {
    const filters = state.request.filters;

    if (!filters[groupId]) {
      filters[groupId] = [filterId];

      return;
    }

    const isFilterSelected = filters[groupId].some((f) => f === filterId);

    if (!isFilterSelected) {
      filters[groupId].push(filterId);
    } else {
      filters[groupId] = filters[groupId].filter((f) => f !== filterId);
    }

    state.request.pageNumber = 1;
  },

  /**
   *
   * @param {SearchState} state
   * @param {import('./state').SearchResponse} response
   */
  [mutationTypes.SET_RESPONSE](state, response) {
    if (!response.groups.length || !response.contentTypeTaxonomies.length) {
      log.error('Empty search response');

      return;
    }

    state.response = response;

    if (
      response.pageNumber === 1 &&
      state.request.orderBy === state.orderByOptions.relevance.value &&
      state.request.term
    ) {
      state.maxRelevance = Math.max(...response.results.map((r) => r.score));
    } else if (
      !state.request.term ||
      state.request.orderBy !== state.orderByOptions.relevance.value
    ) {
      state.maxRelevance = 0;
    }
  },

  /**
   *
   * @param {SearchState} state
   */
  [mutationTypes.RESET_ABORT_CONTROLLER](state) {
    state.abortController = new AbortController();
  },

  /**
   *
   * @param {SearchState} state
   * @param {boolean} isLoading
   */
  [mutationTypes.SET_IS_LOADING](state, isLoading) {
    if (
      (state.actionState === 'INITIALIZING' ||
        state.actionState === 'LOADING') &&
      !isLoading
    ) {
      state.actionState = 'LOADED';

      return;
    }

    if (state.actionState === 'LOADED' && isLoading) {
      state.actionState = 'LOADING';

      return;
    }
  },
};

/**
 * @typedef {import('./state').SearchState} SearchState
 */
